import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
import { HoDDoSAssessmentBox } from './HoDDoSAssessmentBox';
import { HOD_DOS_DECISION } from './constant';
import { PAGE_TEXT } from '../../../lang';
import {
  useGetHoDDoSAssessment,
  useUpdateHoDDoSAssessment,
  type HodDoSAssessment,
  useCreateHoDDoSAssessment,
  useSubmitHoDDoSAssessment,
} from '../../../graphql';
import {
  Accordion,
  Loading,
  Alert,
  type AlertDetails,
  Dialog,
  UserRole,
  type DialogDetails,
} from '../../../../../components';

type HoDDoSAssessmentContentProps = {
  isViewMode?: boolean;
};

export const HoDDoSAssessmentContent: React.FunctionComponent<
  HoDDoSAssessmentContentProps
> = props => {
  const { applicationId = '' } = useParams();
  const { isViewMode = false } = props;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogDetails, setDialogDetails] = useState<DialogDetails>({
    title: '',
    content: '',
  });
  const [openAlert, setOpenAlert] = useState(false);
  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'info',
  });

  const {
    hodDosAssessment,
    remarks: returnRemarks,
    hodRequired,
    loading,
    error: hodDosAssessmentError,
    refetch: refetchHodDosAssessment,
  } = useGetHoDDoSAssessment(applicationId);

  const createHoDDoSAssessment = useCreateHoDDoSAssessment();
  const handleHoDDoSAssessmentCreation = async (): Promise<void> => {
    try {
      await createHoDDoSAssessment({
        variables: {
          applicationId,
        },
      });
      refetchHodDosAssessment();
    } catch (e) {
      setAlertDetails({
        alertContent: 'Error Occurs.' + (e as Error)?.message,
        alertType: 'error',
      });
      setOpenAlert(true);
    }
  };

  useEffect(() => {
    const handleCreateHodDosAssessment = async (): Promise<void> => {
      if (!loading && !isViewMode) {
        // re-create HoD DoS Assessment on every edit mode
        // in order to align with the latest funding
        await handleHoDDoSAssessmentCreation();
      }
    };

    // call the async function
    handleCreateHodDosAssessment().catch(e => {
      setAlertDetails({
        alertContent: 'Error Occurs.' + (e as Error)?.message,
        alertType: 'error',
      });
      setOpenAlert(true);
    });
  }, [loading]);

  if (hodDosAssessmentError) {
    return (
      <Alert
        openAlert
        setOpenAlert={() => {}}
        alertDetails={{
          alertContent:
            `Error Occurs.` + (hodDosAssessmentError as Error)?.message,
          alertType: 'error',
        }}
      />
    );
  }
  const updateHoDDoSAssessment = useUpdateHoDDoSAssessment();
  const handleSaveAndSubmit = async (
    value: HodDoSAssessment,
  ): Promise<void> => {
    const { submitted, ...other } = value;

    try {
      const variablesToUpdate = {
        input: {
          applicationId,
          ...other,
        },
      };
      const variablesToReset = {
        input: {
          applicationId,
          externalGrantOrDEA: {
            required: false,
          },
          nonSTEMOrNonSHS: {
            required: false,
          },
          competitiveFund: {
            required: false,
          },
          dualPhD: {
            required: false,
          },
          decision: HOD_DOS_DECISION.NA,
        },
      };
      // reset the previous hod/dos decision to NA, if hod/dos decision is not required any more
      const variables: any = hodRequired ? variablesToUpdate : variablesToReset;
      await updateHoDDoSAssessment({ variables });
      await confirmSubmitHoDDoSAssessment();
      refetchHodDosAssessment();
    } catch (error: any) {
      setAlertDetails({
        alertContent: `Error Occurs.` + (error as Error)?.message,
        alertType: 'error',
      });
      setOpenAlert(true);
    }
  };

  const handleCloseDialog = (): void => {
    setDialogOpen(false);
  };

  const hoddosRelatedRemark = returnRemarks.filter(
    rmk =>
      rmk.fromRole === UserRole.HOD_DOS ||
      rmk.toRole.includes(UserRole.HOD_DOS),
  );

  useEffect(() => {
    if (hoddosRelatedRemark.length && !loading && !isViewMode) {
      setDialogDetails(
        dialogMappings[PAGE_TEXT.HOD_DOS_ASSESSMENT.PREVIEW_DIALOG.TITLE],
      );
      setDialogOpen(true);
    }
  }, [loading]);

  const submitHoDDoSAssessment = useSubmitHoDDoSAssessment();
  const confirmSubmitHoDDoSAssessment = async (): Promise<void> => {
    setDialogOpen(false);
    try {
      await submitHoDDoSAssessment({
        variables: { applicationId },
      });
      setDialogDetails(dialogMappings[PAGE_TEXT.CLOSE]);
      setDialogOpen(true);
    } catch (error) {
      setAlertDetails({
        alertContent: `Error Occurs.` + (error as Error)?.message,
        alertType: 'error',
      });
      setOpenAlert(true);
    }
  };

  const handleBack = (): void => {
    window.location.href = '/internal/hod-dos-homepage/hoddosassessments';
  };

  const dialogMappings: Record<string, DialogDetails> = {
    [PAGE_TEXT.HOD_DOS_ASSESSMENT.PREVIEW_DIALOG.TITLE]: {
      title: PAGE_TEXT.HOD_DOS_ASSESSMENT.PREVIEW_DIALOG.TITLE,
      content: PAGE_TEXT.HOD_DOS_ASSESSMENT.SUBMIT_CONFIRM.CONTENT,
      confirmAction: PAGE_TEXT.OK,
      cancelAction: undefined,
      handleDialogConfirmAction: handleCloseDialog,
    },

    // on submit successfully
    [PAGE_TEXT.CLOSE]: {
      title: '',
      content: PAGE_TEXT.HOD_DOS_ASSESSMENT.SUBMIT_SUCCESS,
      confirmAction: PAGE_TEXT.BACK,
      handleDialogConfirmAction: handleBack,
    },
  };

  return (
    <div>
      <ContentBox>
        <Alert
          openAlert={openAlert}
          setOpenAlert={setOpenAlert}
          alertDetails={alertDetails}
        />
        {dialogOpen && (
          <Dialog
            openDialog={dialogOpen}
            width={'700px'}
            dialogTitle={dialogDetails.title}
            dialogAction={dialogDetails.confirmAction}
            cancelAction={dialogDetails.cancelAction ?? undefined}
            onDialogAction={dialogDetails.handleDialogConfirmAction}
            onDialogClose={handleCloseDialog}
          >
            <Typography margin={'0rem 1.5rem 2rem'}>
              {dialogDetails.content}
            </Typography>
          </Dialog>
        )}
        {loading || hodDosAssessment === null ? (
          <Loading />
        ) : (
          <Accordion
            className="w-full"
            defaultExpanded
            title={PAGE_TEXT.HOD_DOS_ASSESSMENT.ASSESSMENT.TITLE}
            details={
              <div className="mb-8">
                <HoDDoSAssessmentBox
                  initialValues={hodDosAssessment}
                  handleSaveAndSubmit={handleSaveAndSubmit}
                  hodRequired={hodRequired}
                  isViewMode={isViewMode}
                  remarks={hoddosRelatedRemark}
                />
              </div>
            }
          />
        )}
      </ContentBox>
    </div>
  );
};

const ContentBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;

  Accordion {
    margin-bottom: 1rem;
  }
`;
